<template>
  <!-- XOL-586 (F3, Q7, Q9, Q10): the request's Activity log, in the Activity popup's drawer
       (Tower Co only; a view-only Tower Co user can open it). One entry per change to the
       request, newest first, grouped by day (Today / Yesterday / the date): GET
       /actions/{id}/activity, 25 to a page, filtered by step and by type.

       Delta 4: a feed, not a table. Each entry is a row of time, a kind icon, the sentence, its
       meta line (who, step) and, for an e-mail, a status tag. An entry that has a dialog is ONE
       real <button> (the whole row): an e-mail line opens the read-only MailBatchDialog, and a
       field or recipient edit (`has_values`) opens ActivityChangesDialog, which loads the before
       / after values itself, when it opens. The list carries no values and neither does this
       component.

       Layout: the filter bar is FIXED above the list and only `.activity-scroll` (the feed, the
       empty / error / loading states, the footnote and the pager) scrolls. Popup.vue's body is a
       plain scroll box (padded, 450px max-height) and is not changed for this, so the log MEASURES
       the room the body has and caps `.activity-scroll` with an inline max-height (measureScroll).
       If it cannot measure, the cap is left unset and the whole body scrolls, as before.

       The log starts empty on deploy (no backfill, Q7) and has no "Reconstructed" label.
       On the last page of the unfiltered log, a footnote ends the rail and says so. -->
  <div class="activity-log" role="region" :aria-label="text('requestActivity.region')">

    <div class="activity-filters" ref="filters" :class="{ 'is-narrow': isMediumOrLess }">
      <!-- Element's select does not pass aria-label to its input (it lands on the wrapper div), so the
           input takes an id and a visually hidden <label for> names it. -->
      <label :for="stepFilterId" class="visually-hidden">{{text('requestActivity.filter.step.label')}}</label>
      <el-select class="activity-filters__step" v-model="stepFilter" size="small" :id="stepFilterId"
        @change="onFilterChange">
        <el-option v-for="o in stepOptions" :key="'s' + o.value" :label="o.label" :value="o.value" />
      </el-select>

      <label :for="typeFilterId" class="visually-hidden">{{text('requestActivity.filter.type.label')}}</label>
      <el-select class="activity-filters__type" v-model="typeFilter" size="small" :id="typeFilterId"
        @change="onFilterChange">
        <el-option v-for="o in typeOptions" :key="'t' + o.value" :label="o.label" :value="o.value" />
      </el-select>

      <span class="activity-filters__spacer"></span>

      <el-button class="activity-filters__refresh" size="small" type="success" plain icon="fa fa-sync-alt"
        :circle="isMediumOrLess"
        :disabled="loading"
        :aria-label="isMediumOrLess ? text('requestActivity.refresh.aria') : null"
        @click="load">
        <span v-if="!isMediumOrLess">{{text('requestActivity.refresh')}}</span>
      </el-button>
    </div>

    <!-- Everything below the filter bar scrolls; the filter bar does not. -->
    <div class="activity-scroll" ref="scroll" :style="scrollStyle">

    <!-- Error: the filters stay; the feed and the pager give way to one alert. -->
    <div v-if="error" class="activity-error">
      <el-alert v-if="error === 'forbidden'" type="error" show-icon :closable="false"
        :title="text('requestActivity.error.forbidden', { towerCo: towerCo })" />
      <template v-else>
        <el-alert type="error" show-icon :closable="false"
          :title="text('requestActivity.error.title')"
          :description="text('requestActivity.error.body')" />
        <el-button size="small" class="activity-error__retry" @click="load">{{text('requestActivity.error.retry')}}</el-button>
      </template>
    </div>

    <template v-else>

      <div class="activity-feed" :class="{ 'is-phone': isMediumOrLess, 'has-end': showStartNote }"
        v-loading="loading" :element-loading-text="text('requestActivity.loading')">

        <section v-for="(day, di) in days" :key="day.key + '-' + di" class="activity-day"
          :aria-labelledby="day.label ? dayId(di) : null">
          <h5 v-if="day.label" class="activity-day__head" :id="dayId(di)">{{day.label}} <small v-if="day.sub">{{day.sub}}</small></h5>

          <ol class="activity-day__list">
            <li v-for="row in day.rows" :key="row.id" class="activity-day__item">

              <!-- A row with a dialog is ONE button (the whole row); a row without one is a div. -->
              <component :is="row.dialog ? 'button' : 'div'" class="activity-entry"
                :class="{ 'is-link': !!row.dialog }"
                :type="row.dialog ? 'button' : null"
                :aria-haspopup="row.dialog ? 'dialog' : null"
                :aria-label="row.dialog ? entryAria(row) : null"
                @click="onEntryClick(row)">

                <span v-if="!isMediumOrLess" class="activity-entry__time">{{row.time}}</span>

                <span class="activity-entry__icon" :class="'is-' + row.tone" aria-hidden="true"><i class="fa" :class="row.icon"></i></span>

                <span class="activity-entry__main">
                  <span class="activity-entry__text" aria-hidden="true"><template v-for="(p, pi) in row.parts"><b v-if="p.tone === 'obj' || p.tone === 'file'" :key="pi" class="activity-part" :class="'is-' + p.tone" :title="p.file || null">{{partText(p, fileMax)}}</b><span v-else :key="pi" class="activity-part" :class="'is-' + p.tone">{{p.text}}</span></template></span>
                  <span class="visually-hidden">{{row.text}}</span>

                  <span class="activity-entry__meta">
                    <el-tag v-if="row.tag && isMediumOrLess" class="activity-entry__tag-inline" size="mini" effect="light" :type="row.tag.type"><i class="fa" :class="row.tag.icon" aria-hidden="true"></i><span>{{row.tag.text}}</span></el-tag>
                    <template v-if="isMediumOrLess"><span class="activity-entry__stamp">{{row.time}}</span><span class="activity-entry__sep"> · </span></template>
                    <span v-if="row.who.system" class="activity-entry__who"><i class="fa fa-cog" aria-hidden="true"></i>{{row.who.text}}</span>
                    <span v-else-if="row.who.name" class="activity-entry__who">{{row.who.name}}<span v-if="row.who.org" class="activity-entry__org"> ({{row.who.org}})</span></span>
                    <span v-else class="activity-entry__who">{{row.who.text}}</span>
                    <span class="activity-entry__sep"> · </span><span class="activity-entry__step">{{row.step.text}}</span>
                  </span>
                </span>

                <span v-if="!isMediumOrLess" class="activity-entry__tag">
                  <el-tag v-if="row.tag" size="mini" effect="light" :type="row.tag.type"><i class="fa" :class="row.tag.icon" aria-hidden="true"></i><span>{{row.tag.text}}</span></el-tag>
                </span>

                <span class="activity-entry__chevron" aria-hidden="true"><i v-if="row.dialog" class="fa fa-angle-right"></i></span>

              </component>
            </li>
          </ol>
        </section>

        <!-- Nothing is said before the first answer: the mask is the whole message. -->
        <div v-if="loaded && days.length === 0 && filtered" class="activity-empty">
          <p class="activity-empty__title"><b>{{text('requestActivity.emptyFiltered.title')}}</b></p>
          <el-button type="text" size="small" @click="clearFilters">{{text('requestActivity.emptyFiltered.clear')}}</el-button>
        </div>
        <div v-else-if="loaded && days.length === 0" class="activity-empty">
          <p class="activity-empty__title"><b>{{text('requestActivity.empty.title')}}</b></p>
          <p class="activity-empty__body">{{text('requestActivity.empty.body')}}</p>
        </div>

        <!-- The end of the rail: the log began after the request did. -->
        <div v-if="showStartNote" class="activity-end">
          <span class="activity-end__icon" aria-hidden="true"><i class="fa fa-history"></i></span>
          <span class="activity-end__text">{{text('requestActivity.end.partial')}}</span>
        </div>

      </div>

      <el-pagination v-if="info && info.total > pageSize"
        class="activity-pager" :class="{ 'is-centered': isMediumOrLess }"
        small hide-on-single-page
        :layout="isMediumOrLess ? 'prev, pager, next' : 'prev, pager, next, total'"
        :pager-count="5"
        :page-size="pageSize"
        :current-page="page"
        :total="info.total"
        @current-change="goToPage" />

    </template>

    </div>

    <!-- Announces the range after each load (the repo has no global sr-only class). -->
    <span class="activity-log__status" role="status">{{rangeText}}</span>

    <mail-batch-dialog
      :visible="mailDialog.visible"
      :action-id="request.id"
      :batch-id="mailDialog.batchId"
      :steps="request.steps || []"
      :rtx="rtx"
      :status="mailDialog.status"
      @close="mailDialog.visible = false" />

    <activity-changes-dialog
      :visible="changesDialog.visible"
      :action-id="request.id"
      :row="changesDialog.row"
      :steps="request.steps || []"
      :rtx="rtx"
      :orgs="orgs"
      :label-ctx="labelCtx"
      @close="changesDialog.visible = false" />

  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import MailBatchDialog from '@/components/requests/MailBatchDialog'
import ActivityChangesDialog from '@/components/requests/ActivityChangesDialog'
import { appNow } from '@/lib/appTime'
import {
  ACTIVITY_PAGE_SIZE,
  activityText,
  activityRow,
  activityDays,
  partText,
  activityQuery,
  activityIsFiltered,
  activityTypeOptions,
  activityStepOptions,
  parseActivityPage,
  activityRangeText,
  activityShowsStartNote
} from '@/lib/requestActivity'

// The list never gets less than this many px, however short the popup body is.
var MIN_SCROLL = 200
// Left unused when measuring, so rounding never makes the body scroll too.
var SAFETY = 2

function shortOf (org) {
  return org ? (org.shortName || org.name || null) : null
}

export default {
  name: 'request-activity-log',

  mixins: [DateMixins],

  components: {
    MailBatchDialog,
    ActivityChangesDialog
  },

  props: {
    // The request (EmailSetup takes the same).
    rowData: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      // '' = all
      stepFilter: '',
      typeFilter: '',
      page: 1,
      pageSize: ACTIVITY_PAGE_SIZE,
      info: null,
      loading: false,
      // The first answer (or refusal) has arrived: before it, no empty state is shown.
      loaded: false,
      // null | 'generic' | 'forbidden'
      error: null,
      seq: 0,
      mailDialog: { visible: false, batchId: null, status: null },
      changesDialog: { visible: false, row: null },
      // The measured max-height (px) of .activity-scroll; null = not measured, no cap.
      scrollMax: null
    }
  },

  computed: {
    scrollStyle () {
      return this.scrollMax ? { maxHeight: this.scrollMax + 'px' } : null
    },

    request () {
      return this.rowData || {}
    },

    rtx () {
      return this.request.actionType || 'request'
    },

    towerCo () {
      return typeof this.towerCoName === 'function' ? this.towerCoName() : 'Tower Co'
    },

    // The organisations a party is called in a sentence.
    orgs () {
      return {
        towerco: activityText('requestMail.party.towerco'),
        owner: shortOf(this.request.organisation),
        provider: this.request.providerShortName || null
      }
    },

    // The names the recipient-list field labels are built from (requestSyncCopy).
    labelCtx () {
      return {
        towerCo: this.towerCo,
        telco: this.orgs.owner || activityText('requestMail.party.owner'),
        provider: this.orgs.provider || activityText('requestMail.party.provider')
      }
    },

    stepFilterId () {
      return 'activity-step-filter-' + this._uid
    },

    typeFilterId () {
      return 'activity-type-filter-' + this._uid
    },

    stepOptions () {
      return activityStepOptions(this.request.steps)
    },

    typeOptions () {
      return activityTypeOptions()
    },

    filtered () {
      return activityIsFiltered({ step: this.stepFilter, type: this.typeFilter })
    },

    rows () {
      return this.info ? this.info.rows : []
    },

    displayRows () {
      var ctx = {
        rtx: this.rtx,
        formatDate: this.formatDate,
        formatDateTime: this.formatDateTime,
        orgs: this.orgs,
        labelCtx: this.labelCtx
      }

      return this.rows.map(function (r) { return activityRow(r, ctx) })
    },

    // The rows of this page grouped by day. "Today" and "Yesterday" are the APP clock's (XOL-414),
    // never the browser's.
    days () {
      return activityDays(this.displayRows, appNow().format('YYYY-MM-DD'))
    },

    // How long a file name may be before it is cut in the middle (the full name is its tooltip).
    fileMax () {
      return this.isMediumOrLess ? 28 : 40
    },

    rangeText () {
      return this.error ? '' : activityRangeText(this.info)
    },

    showStartNote () {
      return !this.loading && activityShowsStartNote(this.info, this.filtered)
    }
  },

  mounted () {
    // The Activity popup renders its body on the FIRST open, so this is the first read.
    // Every later open comes through reload().
    this.load()
    this.measureSoon(true)
    window.addEventListener('resize', this.measureScroll)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.measureScroll)
    clearTimeout(this.measureTimer)
  },

  watch: {
    // The filter bar wraps to two rows on a phone, so its height changes.
    isMediumOrLess () {
      this.measureSoon()
    }
  },

  methods: {
    text (key, params) {
      return activityText(key, params)
    },

    // The popup opened again: page 1 of the log as it is now, with the filters kept.
    reload () {
      this.page = 1
      this.scrollToTop()
      this.measureSoon(true)
      return this.load()
    },

    // Measure after the next render and, when the popup has just opened, once more after its
    // 300ms slide-in (the body is still moving, so the first read can be too early).
    measureSoon (again) {
      var self = this

      this.$nextTick(this.measureScroll)

      if (again) {
        clearTimeout(this.measureTimer)
        this.measureTimer = setTimeout(function () { self.measureScroll() }, 300)
      }
    },

    // Cap .activity-scroll so the filter bar plus the list exactly fill the Popup body, which
    // then never scrolls itself. The body is this component's parent (Popup's `.padded` div).
    // The cap is taken off while measuring: the body is only as tall as its content when that is
    // less than its 450px max-height, so reading it with the old cap on would shrink the cap by
    // the safety margin on every measure. Anything unmeasurable leaves the cap unset.
    measureScroll () {
      if (this._isDestroyed) {
        return
      }

      var box = this.$refs.scroll
      var bar = this.$refs.filters
      var body = this.$el && this.$el.parentElement

      if (!box || !bar || !body) {
        this.scrollMax = null
        return
      }

      var top = box.scrollTop
      var cap = box.style.maxHeight

      box.style.maxHeight = 'none'
      var room = body.clientHeight
      var natural = box.scrollHeight
      box.style.maxHeight = cap
      box.scrollTop = top

      if (!room) {
        this.scrollMax = null
        return
      }

      var bodyStyle = window.getComputedStyle(body)
      var barStyle = window.getComputedStyle(bar)
      var used = (parseFloat(bodyStyle.paddingTop) || 0) + (parseFloat(bodyStyle.paddingBottom) || 0) +
        bar.offsetHeight + (parseFloat(barStyle.marginBottom) || 0) + SAFETY

      var avail = room - used

      // A list that fits (give or take the safety margin) is never capped below its own height,
      // or a body that is only as tall as its content would scroll by 2px.
      if (natural && natural <= avail + SAFETY) {
        avail = natural
      }

      this.scrollMax = Math.max(MIN_SCROLL, Math.floor(avail))
    },

    // A new page or a new filter reads from the top of the list again.
    scrollToTop () {
      var box = this.$refs.scroll

      if (box) {
        box.scrollTop = 0
      }
    },

    load () {
      var self = this
      var seq = ++this.seq

      if (!this.request.id) {
        return Promise.resolve()
      }

      this.loading = true
      this.error = null

      var path = '/actions/' + this.request.id + '/activity?' + activityQuery({
        page: this.page,
        perPage: this.pageSize,
        step: this.stepFilter,
        type: this.typeFilter
      })

      return axios.get(this.getEndpoint(path)).then(function (response) {
        // The latest request wins: an older answer is dropped.
        if (seq !== self.seq) {
          return
        }

        self.info = parseActivityPage(response ? response.data : null, { page: self.page, perPage: self.pageSize })
        self.loading = false
        self.loaded = true
        self.measureSoon()
      }, function (err) {
        if (seq !== self.seq) {
          return
        }

        var status = err && err.response ? err.response.status : null

        console.error('XOL-586: could not load the activity log', status, err)

        self.error = status === 403 ? 'forbidden' : 'generic'
        self.loading = false
        self.loaded = true
        // The alert can change the height of what is above the list's cap.
        self.measureSoon()
      })
    },

    onFilterChange () {
      this.page = 1
      this.scrollToTop()
      this.load()
    },

    clearFilters () {
      this.stepFilter = ''
      this.typeFilter = ''
      this.onFilterChange()
    },

    goToPage (p) {
      this.page = p
      this.scrollToTop()
      this.load()
    },

    partText (p, max) {
      return partText(p, max)
    },

    dayId (i) {
      return 'activity-day-' + i + '-' + this._uid
    },

    // The accessible name of a row that opens a dialog: what happened, its result, who, when, step.
    entryAria (row) {
      return this.text('requestActivity.entry.aria', {
        text: row.text,
        tag: row.tag ? row.tag.text : null,
        who: row.who.text,
        when: row.when,
        step: row.step.text
      })
    },

    // A row without a dialog is a plain div: a click on it opens nothing.
    onEntryClick (row) {
      if (row.dialog) {
        this.openRow(row)
      }
    },

    openRow (row) {
      var raw = this.rawRow(row.id)

      if (row.dialog === 'mail' && row.batchId) {
        this.mailDialog = {
          visible: true,
          batchId: row.batchId,
          status: raw && raw.details && raw.details.approval_status ? raw.details.approval_status : null
        }
      } else if (row.dialog === 'changes' && raw) {
        this.changesDialog = { visible: true, row: raw }
      }
    },

    rawRow (id) {
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].id === id) {
          return this.rows[i]
        }
      }

      return null
    }
  }
}
</script>

<style scoped>
.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

/* The list scrolls in `.activity-scroll`, capped by an inline max-height that measureScroll sets
   from the Popup body's height (see the script). With no cap the whole Popup body scrolls. */
.activity-scroll { overflow-y: auto; }

/* The control band (Recipe C): a light blue bar FIXED above the scrolling list (Carlos,
   2026-10-18). Not sticky: inside the drawer's padded scroll box a sticky bar stopped below the
   top edge and the rows showed above and behind it. So the list scrolls in `.activity-scroll`
   and the bar is outside it. */
.activity-filters { display: flex; align-items: center; gap: 8px; padding: 8px 12px; margin-bottom: 8px; border-radius: 4px; background: #F2F6FC; }
.activity-filters__step, .activity-filters__type { width: 200px; }
.activity-filters__spacer { flex: 1; }
.activity-filters__refresh ::v-deep .fa + span { margin-left: 5px; }
/* Phone: row 1 = Step + the icon-only Refresh, row 2 = Type at full width. */
.activity-filters.is-narrow { flex-wrap: wrap; }
.activity-filters.is-narrow .activity-filters__step { order: 1; flex: 1 1 0; width: auto; }
.activity-filters.is-narrow .activity-filters__refresh { order: 2; }
.activity-filters.is-narrow .activity-filters__spacer { display: none; }
.activity-filters.is-narrow .activity-filters__type { order: 3; flex: 0 0 100%; width: 100%; }

.activity-error { margin-top: 8px; }
.activity-error__retry { margin-top: 8px; }

.activity-feed { min-height: 160px; }

/* A day: its header, then its entries on a rail. The header overrides the silver global h5 on
   purpose: the day is information the user must read. */
.activity-day__head { margin: 16px 0 4px; padding: 0 12px 6px; font-size: 13px; line-height: 20px; font-weight: bold; color: #606266; border-bottom: 1px solid #EBEEF5; }
.activity-day:first-child .activity-day__head { margin-top: 4px; }
.activity-day__head small { margin-left: 2px; font-size: 12px; font-weight: normal; color: #909399; }
.activity-day__list { list-style: none; margin: 0; padding: 0; }
.activity-day__item { margin: 0; padding: 0; }

/* One entry: time | icon | sentence + meta | status tag | chevron. A <button> when it has a dialog. */
.activity-entry { display: grid; grid-template-columns: 64px 28px minmax(0, 1fr) auto 16px; column-gap: 12px; align-items: start; width: 100%; box-sizing: border-box; padding: 8px 12px; margin: 0; border: 0; border-radius: 4px; background: transparent; text-align: left; font: inherit; color: inherit; position: relative; }
.activity-entry > span { display: block; min-width: 0; }
.activity-entry.is-link { cursor: pointer; -webkit-appearance: none; appearance: none; }
.activity-entry.is-link:hover { background: #F5F7FA; }
.activity-entry.is-link:focus { outline: none; }
.activity-entry.is-link:focus-visible { outline: 2px solid #409EFF; outline-offset: -2px; background: #F5F7FA; }
.activity-entry.is-link:hover .fa-angle-right, .activity-entry.is-link:focus-visible .fa-angle-right { color: #409EFF; }

.activity-entry__time { font-size: 12px; line-height: 28px; color: #909399; text-align: right; font-variant-numeric: lining-nums tabular-nums; white-space: nowrap; }
.activity-entry__icon { position: relative; z-index: 1; width: 28px; height: 28px; line-height: 28px; border-radius: 50%; text-align: center; font-size: 13px; background: #F4F4F5; color: #909399; box-shadow: 0 0 0 3px #fff; }
.activity-entry__icon.is-success { background: #F0F9EB; color: #67C23A; }
.activity-entry__icon.is-danger { background: #FEF0F0; color: #F56C6C; }
.activity-entry.is-link:hover .activity-entry__icon, .activity-entry.is-link:focus-visible .activity-entry__icon { box-shadow: 0 0 0 3px #F5F7FA; }

.activity-entry__main { padding-top: 4px; }
.activity-entry__text { font-size: 14px; line-height: 20px; display: block; }
.activity-part.is-lead, .activity-part.is-tail { color: #606266; }
.activity-part.is-obj, .activity-part.is-file, .activity-part.is-plain { color: #303133; }
.activity-part.is-file { overflow-wrap: anywhere; }
.activity-entry__meta { display: block; margin-top: 2px; font-size: 12px; line-height: 18px; color: #909399; }
.activity-entry__who { color: #606266; }
.activity-entry__who .fa { margin-right: 5px; color: #909399; }
.activity-entry__org, .activity-entry__step { color: #909399; }
.activity-entry__tag { margin-top: 4px; line-height: 20px; }
.activity-entry__tag .fa, .activity-entry__tag-inline .fa { margin-right: 5px; }
.activity-entry__tag-inline { margin-right: 6px; }
.activity-entry__stamp { font-variant-numeric: lining-nums tabular-nums; }
.activity-entry__chevron { font-size: 16px; line-height: 28px; text-align: center; color: #C0C4CC; }

/* The rail: one 1px line through the icon column, cut by each icon's white halo. It starts at
   the first icon and ends at the last, and a day with one entry has none. */
.activity-entry::before { content: ''; position: absolute; top: 0; bottom: 0; left: 101.5px; width: 1px; background: #EBEEF5; z-index: 0; }
.activity-day__item:first-child .activity-entry::before { top: 22px; }
.activity-day__item:last-child .activity-entry::before { bottom: auto; height: 22px; }
.activity-day__item:first-child:last-child .activity-entry::before { display: none; }
/* With the end-of-log note below, the last day's rail runs on into it. */
.activity-feed.has-end .activity-day:last-of-type .activity-day__item:last-child .activity-entry::before { bottom: 0; height: auto; display: block; }

/* The end of the rail. */
.activity-end { display: grid; grid-template-columns: 64px 28px minmax(0, 1fr); column-gap: 12px; align-items: start; padding: 0 12px 8px; position: relative; }
.activity-end::before { content: ''; position: absolute; top: 0; height: 14px; left: 101.5px; width: 1px; background: #EBEEF5; }
.activity-end__icon { grid-column: 2; position: relative; z-index: 1; margin-top: 4px; width: 28px; height: 28px; line-height: 26px; border-radius: 50%; text-align: center; font-size: 13px; color: #909399; background: #fff; border: 1px dashed #DCDFE6; box-sizing: border-box; }
.activity-end__text { grid-column: 3; padding-top: 4px; font-size: 12px; line-height: 28px; color: #606266; }

.activity-empty { padding: 32px 16px; text-align: center; }
.activity-empty__title { margin: 0; font-size: 14px; line-height: 20px; color: #303133; }
.activity-empty__body { margin: 4px auto 0; font-size: 13px; line-height: 20px; color: #606266; max-width: 460px; }

.activity-pager { margin-top: 12px; text-align: right; padding: 0; }
.activity-pager.is-centered { text-align: center; }

/* Phone: no time column (the time leads the meta line), a narrower grid, a tighter rail. */
.activity-feed.is-phone .activity-entry { grid-template-columns: 28px minmax(0, 1fr) 16px; column-gap: 10px; padding: 8px 4px; }
.activity-feed.is-phone .activity-entry::before { left: 17.5px; }
.activity-feed.is-phone .activity-entry__chevron { align-self: center; }
.activity-feed.is-phone .activity-end { grid-template-columns: 28px minmax(0, 1fr); column-gap: 10px; padding: 0 4px 8px; }
.activity-feed.is-phone .activity-end::before { left: 17.5px; }
.activity-feed.is-phone .activity-end__icon { grid-column: 1; }
.activity-feed.is-phone .activity-end__text { grid-column: 2; }

/* Visually hidden, read out by a screen reader (the repo has no global sr-only class). */
.activity-log__status { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
</style>
